import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../../context/UserContext';
import ApiService from '../../../services/ApiService';
import CityAutocomplete from '../../../components/CityAutocomplete/CityAutocomplete';
import DanceStyleSearch from '../../../components/DanceStyleSearch/DanceStyleSearch';
import MonthYearPicker from '../../../components/MonthYearPicker/MonthYearPicker';
import ProfilePhotoUpload from '../../../components/ProfilePhotoUpload/ProfilePhotoUpload';

const LEVEL_OPTIONS = [
  { value: '', label: 'Select level' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'not-interested', label: 'Not interested' },
];

const SettingsSection = ({ profileData, setProfileData, setUser }) => {
  const navigate = useNavigate();
  const { setUser: setContextUser } = useContext(UserContext);

  const [isEditing, setIsEditing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    location: false,
    danceStyles: false,
  });
  const [formData, setFormData] = useState({
    fullName: '',
    danceAlias: '',
    city: '',
    moveMuse: '',
  });
  const [dances, setDances] = useState([]);
  const [showAddDance, setShowAddDance] = useState(false);
  const [currentDance, setCurrentDance] = useState({
    style: '',
    since: '',
    follow: '',
    lead: '',
  });
  const [danceErrors, setDanceErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});

  // Newsletter
  const [newsletterConsent, setNewsletterConsent] = useState(profileData?.newsletterConsent || false);
  const [savingNewsletter, setSavingNewsletter] = useState(false);

  const handleNewsletterToggle = async () => {
    const newValue = !newsletterConsent;
    setSavingNewsletter(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          newsletterConsent: newValue,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setNewsletterConsent(newValue);
        setMessage({ type: 'success', text: newValue ? 'Subscribed to newsletter!' : 'Unsubscribed from newsletter.' });
      }
    } catch (error) {
      console.error('Error updating newsletter preference:', error);
    } finally {
      setSavingNewsletter(false);
    }
  };

  // Account deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeactivateAccount = async () => {
    setDeleting(true);
    try {
      const res = await ApiService.delete('auth/deactivate-account');
      if (res.data?.success) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        setContextUser(null);
        navigate('/');
      }
    } catch (error) {
      console.error('Deactivation error:', error);
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  // Profile picture
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const fileInputRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  // Character limits
  const MOVE_MUSE_LIMIT = 70;
  const DANCE_ALIAS_LIMIT = 25;

  useEffect(() => {
    if (profileData) {
      setFormData({
        fullName: profileData.fullName || '',
        danceAlias: profileData.danceAlias || '',
        city: profileData.city || '',
        moveMuse: profileData.moveMuse || '',
      });
      setDances(profileData.dances || []);
    }
  }, [profileData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Check character limits
    if (name === 'moveMuse' && value.length > MOVE_MUSE_LIMIT) return;
    if (name === 'danceAlias' && value.length > DANCE_ALIAS_LIMIT) return;

    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCityChange = (e) => {
    const value = e.target ? e.target.value : e;
    setFormData(prev => ({ ...prev, city: value }));
  };

  // Dance style handlers
  const handleCurrentDanceChange = (field, value) => {
    setCurrentDance(prev => ({ ...prev, [field]: value }));
    if (danceErrors[field]) {
      setDanceErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateCurrentDance = () => {
    const newErrors = {};
    if (!currentDance.style) newErrors.style = 'Please select a dance style';
    if (!currentDance.since) newErrors.since = 'Please select when you started';
    if (!currentDance.follow && !currentDance.lead) {
      newErrors.level = 'Please select at least one role level';
    }
    if (currentDance.follow === 'not-interested' && currentDance.lead === 'not-interested') {
      newErrors.level = 'You must select a level for at least one role';
    }
    if (currentDance.style && dances.some(d => d.style?.toLowerCase() === currentDance.style.toLowerCase())) {
      newErrors.style = 'This dance style is already added';
    }
    setDanceErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatSinceForApi = (since) => {
    if (!since) return '';
    const [year, month] = since.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Auto-save dances to backend
  const saveDancesToBackend = async (updatedDances) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ dances: updatedDances }),
      });

      const data = await res.json();
      if (data.success) {
        setProfileData(prev => ({ ...prev, dances: updatedDances }));
        setUser(prev => ({ ...prev, dances: updatedDances }));
        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        userData.dances = updatedDances;
        localStorage.setItem('userData', JSON.stringify(userData));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving dances:', error);
      return false;
    }
  };

  const handleAddDance = async () => {
    if (!validateCurrentDance()) return;

    const newDance = {
      style: currentDance.style,
      years: formatSinceForApi(currentDance.since),
      since: currentDance.since,
      follow: currentDance.follow === 'not-interested' ? '' : currentDance.follow,
      lead: currentDance.lead === 'not-interested' ? '' : currentDance.lead,
    };

    const updatedDances = [...dances, newDance];
    setDances(updatedDances);
    setCurrentDance({ style: '', since: '', follow: '', lead: '' });
    setShowAddDance(false);
    setDanceErrors({});

    // Auto-save to backend
    const saved = await saveDancesToBackend(updatedDances);
    if (saved) {
      setMessage({ type: 'success', text: 'Dance style added!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: 'Failed to save dance style' });
    }
  };

  const handleRemoveDance = async (index) => {
    const updatedDances = dances.filter((_, i) => i !== index);
    setDances(updatedDances);

    // Auto-save to backend
    const saved = await saveDancesToBackend(updatedDances);
    if (saved) {
      setMessage({ type: 'success', text: 'Dance style removed!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: 'Failed to remove dance style' });
    }
  };

  const formatSinceDate = (dance) => {
    if (dance.years) return dance.years;
    if (!dance.since) return '';
    const [year, month] = dance.since.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month, 10) - 1]} ${year}`;
  };

  const formatLevel = (dance) => {
    const parts = [];
    if (dance.follow && dance.follow !== 'not-interested') parts.push(`Follow: ${dance.follow}`);
    if (dance.lead && dance.lead !== 'not-interested') parts.push(`Lead: ${dance.lead}`);
    return parts.join(' | ') || 'No role specified';
  };

  // Photo upload
  const handlePhotoClick = () => {
    if (isEditing) {
      setShowPhotoEditor(true);
    }
  };

  const handlePhotoUploadComplete = async (imageUrl) => {
    if (!imageUrl) return;

    try {
      const token = localStorage.getItem('token');
      const updateRes = await fetch(`${API_BASE}/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ profilePicture: imageUrl }),
      });

      const updateData = await updateRes.json();
      if (updateData.success) {
        setProfileData(prev => ({ ...prev, profilePicture: imageUrl }));
        setUser(prev => ({ ...prev, profilePicture: imageUrl }));
        setMessage({ type: 'success', text: 'Profile photo updated!' });
      }
    } catch (error) {
      console.error('Photo update error:', error);
      setMessage({ type: 'error', text: 'Failed to update photo' });
    }

    setShowPhotoEditor(false);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be less than 5MB' });
      return;
    }

    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const res = await fetch(`${API_BASE}/upload-image/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formDataUpload,
      });

      const data = await res.json();
      if (data.success && data.url) {
        // Update profile with new photo
        const updateRes = await fetch(`${API_BASE}/auth/update-profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ profilePicture: data.url }),
        });

        const updateData = await updateRes.json();
        if (updateData.success) {
          setProfileData(prev => ({ ...prev, profilePicture: data.url }));
          setUser(prev => ({ ...prev, profilePicture: data.url }));
          setMessage({ type: 'success', text: 'Profile photo updated!' });
        }
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to upload photo' });
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      setMessage({ type: 'error', text: 'Failed to upload photo' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage({ type: '', text: '' });
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original data
    if (profileData) {
      setFormData({
        fullName: profileData.fullName || '',
        danceAlias: profileData.danceAlias || '',
        city: profileData.city || '',
        moveMuse: profileData.moveMuse || '',
      });
      setDances(profileData.dances || []);
    }
    setShowAddDance(false);
    setCurrentDance({ style: '', since: '', follow: '', lead: '' });
    setMessage({ type: '', text: '' });
  };

  const handleSave = async () => {
    if (!formData.fullName.trim()) {
      setErrors({ fullName: 'Full name is required' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          danceAlias: formData.danceAlias,
          city: formData.city,
          moveMuse: formData.moveMuse,
          dances: dances,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setProfileData(prev => ({ ...prev, ...data.user }));
        setUser(prev => ({ ...prev, ...data.user }));

        const userData = JSON.parse(localStorage.getItem('userData') || '{}');
        Object.assign(userData, data.user);
        localStorage.setItem('userData', JSON.stringify(userData));

        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        setIsEditing(false);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save settings.' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="section-content settings-section">
      {/* Header with User Info */}
      <div className="settings-header">
        <div className="user-header">
          <div
            className={`user-photo ${isEditing ? 'editable' : ''}`}
            onClick={handlePhotoClick}
          >
            {profileData?.profilePicture ? (
              <img src={profileData.profilePicture} alt={profileData?.fullName} />
            ) : (
              <div className="photo-placeholder">
                {(profileData?.fullName || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            {isEditing && (
              <div className="photo-overlay">
                {uploadingPhoto ? (
                  <span className="spinner-small"></span>
                ) : (
                  <i className="bi bi-camera"></i>
                )}
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoChange}
            style={{ display: 'none' }}
          />
          <div className="user-info">
            <h1>{profileData?.fullName || 'User'}</h1>
            {profileData?.danceAlias && (
              <span className="user-alias">@{profileData.danceAlias}</span>
            )}
          </div>
        </div>

        {!isEditing ? (
          <button className="edit-btn" onClick={handleEdit}>
            <i className="bi bi-pencil"></i>
            Edit
          </button>
        ) : (
          <div className="edit-actions">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="save-btn" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <span className="spinner-small"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`settings-message ${message.type}`}>
          <i className={`bi ${message.type === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle'}`}></i>
          {message.text}
        </div>
      )}

      {/* Collapsible Sections */}
      <div className="settings-sections">
        {/* Basic Info */}
        <div className="collapsible-section">
          <button
            className={`section-toggle ${expandedSections.basicInfo ? 'expanded' : ''}`}
            onClick={() => toggleSection('basicInfo')}
          >
            <div className="toggle-left">
              <i className="bi bi-person"></i>
              <span>Basic Information</span>
            </div>
            <i className={`bi bi-chevron-${expandedSections.basicInfo ? 'up' : 'down'}`}></i>
          </button>

          {expandedSections.basicInfo && (
            <div className="section-content-inner">
              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Your full name"
                  className={errors.fullName ? 'has-error' : ''}
                />
                {errors.fullName && <span className="field-error">{errors.fullName}</span>}
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={profileData?.email || ''}
                  disabled
                  className="email-field"
                />
              </div>

              <div className="form-group">
                <label>
                  Dance Alias
                  <span className="char-counter">{formData.danceAlias.length}/{DANCE_ALIAS_LIMIT}</span>
                </label>
                <input
                  type="text"
                  name="danceAlias"
                  value={formData.danceAlias}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Your dance nickname"
                  maxLength={DANCE_ALIAS_LIMIT}
                />
              </div>

              <div className="form-group">
                <label>
                  "I Move n Muse..."
                  <span className="char-counter">{formData.moveMuse.length}/{MOVE_MUSE_LIMIT}</span>
                </label>
                <textarea
                  name="moveMuse"
                  value={formData.moveMuse}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="What inspires your dance journey?"
                  rows={2}
                  maxLength={MOVE_MUSE_LIMIT}
                />
              </div>
            </div>
          )}
        </div>

        {/* Location */}
        <div className="collapsible-section">
          <button
            className={`section-toggle ${expandedSections.location ? 'expanded' : ''}`}
            onClick={() => toggleSection('location')}
          >
            <div className="toggle-left">
              <i className="bi bi-geo-alt"></i>
              <span>Location</span>
            </div>
            <i className={`bi bi-chevron-${expandedSections.location ? 'up' : 'down'}`}></i>
          </button>

          {expandedSections.location && (
            <div className="section-content-inner">
              <div className="form-group">
                {isEditing ? (
                  <CityAutocomplete
                    value={formData.city}
                    onChange={handleCityChange}
                    label="Where do you dance now?"
                    placeholder="Search for a city..."
                  />
                ) : (
                  <>
                    <label>Where do you dance now?</label>
                    <div className="static-value">{formData.city || 'Not set'}</div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Dance Styles */}
        <div className="collapsible-section">
          <button
            className={`section-toggle ${expandedSections.danceStyles ? 'expanded' : ''}`}
            onClick={() => toggleSection('danceStyles')}
          >
            <div className="toggle-left">
              <i className="bi bi-music-note-beamed"></i>
              <span>My Dance Styles</span>
              {dances.length > 0 && <span className="badge">{dances.length}</span>}
            </div>
            <i className={`bi bi-chevron-${expandedSections.danceStyles ? 'up' : 'down'}`}></i>
          </button>

          {expandedSections.danceStyles && (
            <div className="section-content-inner">
              {/* Dance List */}
              {dances.length > 0 ? (
                <div className="dance-list">
                  {dances.map((dance, index) => (
                    <div key={index} className="dance-item">
                      <div className="dance-info">
                        <span className="dance-name">{dance.style}</span>
                        <span className="dance-details">
                          Since {formatSinceDate(dance)} | {formatLevel(dance)}
                        </span>
                      </div>
                      {isEditing && (
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveDance(index)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No dance styles added yet</div>
              )}

              {/* Add Dance Form */}
              {isEditing && (
                <>
                  {!showAddDance ? (
                    <button
                      className="add-dance-toggle"
                      onClick={() => setShowAddDance(true)}
                    >
                      <i className="bi bi-plus-circle"></i>
                      Add Dance Style
                    </button>
                  ) : (
                    <div className="add-dance-form">
                      <div className="form-group">
                        <DanceStyleSearch
                          value={currentDance.style}
                          onChange={(style) => handleCurrentDanceChange('style', style)}
                          label="Dance Style"
                          placeholder="Search for a dance style..."
                          error={danceErrors.style}
                        />
                      </div>

                      <div className="form-group">
                        <MonthYearPicker
                          value={currentDance.since}
                          onChange={(since) => handleCurrentDanceChange('since', since)}
                          label="Dancing Since"
                          placeholder="Select when you started..."
                          error={danceErrors.since}
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Follow Level</label>
                          <select
                            value={currentDance.follow}
                            onChange={(e) => handleCurrentDanceChange('follow', e.target.value)}
                            className={danceErrors.level ? 'has-error' : ''}
                          >
                            {LEVEL_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Lead Level</label>
                          <select
                            value={currentDance.lead}
                            onChange={(e) => handleCurrentDanceChange('lead', e.target.value)}
                            className={danceErrors.level ? 'has-error' : ''}
                          >
                            {LEVEL_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {danceErrors.level && <span className="field-error">{danceErrors.level}</span>}

                      <div className="add-dance-actions">
                        <button className="cancel-add" onClick={() => {
                          setShowAddDance(false);
                          setCurrentDance({ style: '', since: '', follow: '', lead: '' });
                          setDanceErrors({});
                        }}>
                          Cancel
                        </button>
                        <button className="confirm-add" onClick={handleAddDance}>
                          <i className="bi bi-plus"></i> Add
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Newsletter Preference */}
      <div className="newsletter-toggle-row">
        <div className="newsletter-toggle-info">
          <i className="bi bi-envelope"></i>
          <div>
            <span className="newsletter-toggle-label">Newsletter</span>
            <span className="newsletter-toggle-desc">Receive community insights and updates</span>
          </div>
        </div>
        <button
          className={`newsletter-toggle-btn ${newsletterConsent ? 'active' : ''}`}
          onClick={handleNewsletterToggle}
          disabled={savingNewsletter}
        >
          <span className="newsletter-toggle-knob"></span>
        </button>
      </div>

      {/* Delete Account */}
      <div className="delete-account-row">
        <button
          className="delete-account-btn"
          onClick={() => setShowDeleteModal(true)}
        >
          Delete My Account
        </button>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="ban-modal-overlay" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="ban-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="ban-modal-close" onClick={() => setShowDeleteModal(false)}>&times;</button>
            <div className="ban-modal-header">
              <i className="bi bi-exclamation-triangle" style={{ color: '#dc3545' }}></i>
              <h3>Delete Your Account</h3>
            </div>
            <p className="ban-modal-description">
              This action is irreversible. You will lose access to your account and will not be able to log back in. Your contributions will remain visible to the community.
            </p>
            <div className="ban-modal-actions">
              <button
                className="ban-btn-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="ban-btn-submit"
                onClick={handleDeactivateAccount}
                disabled={deleting}
              >
                {deleting ? (
                  <><span className="spinner-tiny"></span> Deleting...</>
                ) : (
                  "That's good with me. Bye bye!"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Editor Modal */}
      {showPhotoEditor && (
        <div className="photo-editor-modal-overlay">
          <div className="photo-editor-modal">
            <div className="photo-editor-header">
              <h3>Update Profile Photo</h3>
              <button
                className="close-modal-btn"
                onClick={() => setShowPhotoEditor(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="photo-editor-content">
              <ProfilePhotoUpload
                value={profileData?.profilePicture || ''}
                onChange={handlePhotoUploadComplete}
                label=""
                maxSizeMB={5}
                minDimension={320}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsSection;
